'use client';

import React, { createContext, useContext, useMemo } from 'react';
import { canFetchAdminProtectedApis, useAuthStore } from '@/lib/auth-store';
import { getEffectiveRole, isAdminRole } from '@/lib/rbac';
import { useAdminBadges } from '@/hooks/useAdminBadges';
import { ADMIN_BADGE_KEYS, type AdminBadgeKey, type AdminBadgeMap } from '@/lib/admin-badges';

type AdminBadgesContextValue = {
  badges: AdminBadgeMap;
  loading: boolean;
};

const ALL_ADMIN_BADGE_KEYS: AdminBadgeKey[] = [
  ADMIN_BADGE_KEYS.SUPPORT_UNRESOLVED,
  ADMIN_BADGE_KEYS.APPROVALS_PENDING,
  ADMIN_BADGE_KEYS.EVENTS_PENDING,
  ADMIN_BADGE_KEYS.ORGANIZER_REQUESTS_PENDING,
  ADMIN_BADGE_KEYS.REFUNDS_PENDING,
  ADMIN_BADGE_KEYS.VIOLATIONS_ACTIVE,
  ADMIN_BADGE_KEYS.NOTIFICATIONS_UNREAD,
  ADMIN_BADGE_KEYS.PAYOUTS_PENDING,
  ADMIN_BADGE_KEYS.MESSAGES_UNREAD,
  ADMIN_BADGE_KEYS.REVIEWS_PENDING,
  ADMIN_BADGE_KEYS.ESCALATIONS_OPEN,
  ADMIN_BADGE_KEYS.CASES_BREACHED,
  ADMIN_BADGE_KEYS.KYC_PENDING,
  ADMIN_BADGE_KEYS.SETTLEMENTS_PENDING,
];

const AdminBadgesContext = createContext<AdminBadgesContextValue | null>(null);

export function AdminBadgesProvider({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((state) => state.user);
  const authChecking = useAuthStore((state) => state.authChecking);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isAdmin = isAdminRole(getEffectiveRole(user));
  const keys = useMemo(() => ALL_ADMIN_BADGE_KEYS, []);
  const { badges, loading } = useAdminBadges(keys, {
    enabled:
      isAdmin && canFetchAdminProtectedApis({ user, authChecking, isAuthenticated }),
    refreshMs: 60_000,
  });

  const value = useMemo(() => ({ badges, loading }), [badges, loading]);

  return <AdminBadgesContext.Provider value={value}>{children}</AdminBadgesContext.Provider>;
}

export function useAdminBadgesContext(): AdminBadgesContextValue {
  const context = useContext(AdminBadgesContext);
  if (!context) {
    throw new Error('useAdminBadgesContext must be used within AdminBadgesProvider');
  }
  return context;
}

export function useOptionalAdminBadgesContext(): AdminBadgesContextValue | null {
  return useContext(AdminBadgesContext);
}
